import { APP_CUSTOM_SCHEME } from "@src/mobile/utils/deepLinkHandler";
import { Platform } from "@src/mobile/utils/platform";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk in auth redirect handler
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { z } from "zod";

import { MobileAuthLoading } from "../../../mobile/Login/MobileAuthLoading";
import { APP_V2_APP_EVENTS, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { isSafeRedirectUrl } from "../cognito/isSafeRedirectUrl";
import {
  getMagicLinkAuthContext,
  getMagicLinkEmail,
  type MagicLinkAuthContext,
} from "../cognito/magicLinkEmailStore";
import { recordTerminalLoginFailure } from "../loginJourneyTelemetry";
import { isNetworkFailure, logAuthRedirectFailure, LOGIN_OUTCOME, LOGIN_REASON } from "../loginRum";
import { AuthRedirectView } from "./AuthRedirectView";
import { EmailInputView } from "./EmailInputView";
import { useMagicLinkRedeem } from "./hooks/useMagicLinkRedeem";
import { useResolveInitialAuthState } from "./hooks/useResolveInitialAuthState";
import { EMAIL_LOGIN_LINK_PATH } from "./paths";
import { type AuthState } from "./types";

const DEFAULT_REDIRECT_PATH = "/";

const emailSchema = z.string().email();

function isValidEmail(value: string): boolean {
  return emailSchema.safeParse(value).success;
}

export function AuthRedirect() {
  const location = useLocation();
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const [authState, setAuthState] = useState<AuthState>("checking-session");
  const [error, setError] = useState<string | undefined>();
  const [existingEmail, setExistingEmail] = useState<string | undefined>();
  const [emailInputError, setEmailInputError] = useState<string | undefined>();
  const hasAttemptedAuth = useRef(false);
  const hasLoggedPromptEvent = useRef(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const payload = searchParams.get("payload");

  // Remove sensitive token from the URL immediately on mount.
  useEffect(() => {
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
  }, []);

  const handleGoToDashboard = useCallback(() => {
    const requestedUrl = searchParams.get("requestedUrl");
    const target =
      requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;
    history.replace(target);
  }, [history, searchParams]);

  const handleContinue = useMagicLinkRedeem({
    payload,
    searchParams,
    hasAttemptedAuth,
    setAuthState,
    setError,
    dispatch,
  });

  const handleSignOutAndContinue = useCallback(
    async (authContext: MagicLinkAuthContext) => {
      try {
        setAuthState("signing-in");
        // Do NOT sign out of Cognito here: completeMagicLinkSignIn →
        // initiateCustomAuth handles UserAlreadyAuthenticatedException
        // internally (signOut → retry). Calling cognitoSignOut()
        // separately can leave Amplify in a state where the new session
        // tokens aren't persisted to localStorage.
        setExistingEmail(undefined);
        hasAttemptedAuth.current = false;
        await handleContinue(authContext, "cache");
      } catch (signOutError: unknown) {
        const message = "Unable to sign in. Please try again.";
        // A connectivity drop during the sign-out-and-retry is just as
        // transient as in the main redeem path, so classify it as an expected
        // network failure — `logAuthRedirectFailure` then demotes it to a
        // non-error action instead of polluting Error Tracking (STAFF-1354).
        const isNetworkError = isNetworkFailure(signOutError);
        recordTerminalLoginFailure();
        logAuthRedirectFailure({
          error: signOutError,
          metadata: {
            stage: "sign-out-and-continue",
            outcome: isNetworkError ? LOGIN_OUTCOME.EXPECTED_ERROR : LOGIN_OUTCOME.UNEXPECTED_ERROR,
            reason: isNetworkError
              ? LOGIN_REASON.NETWORK_FAILURE
              : LOGIN_REASON.SESSION_HYDRATION_FAILED,
          },
        });
        setError(message);
        setAuthState("error");
        showErrorToast(message);
      }
    },
    [handleContinue]
  );

  const handleSubmitEmail = useCallback(
    (typedEmail: string) => {
      const trimmed = typedEmail.trim();
      if (!isValidEmail(trimmed)) {
        setEmailInputError("Enter a valid email address.");
        return;
      }
      setEmailInputError(undefined);
      void handleContinue({ email: trimmed }, "prompt");
    },
    [handleContinue]
  );

  useResolveInitialAuthState({
    payload,
    handleContinue,
    handleGoToDashboard,
    setAuthState,
    setExistingEmail,
  });

  // Emit a telemetry event the first time we show the prompt so we can alarm
  // if the prompt rate spikes (likely indicates the cache path silently broke).
  useEffect(() => {
    if (authState === "email-input" && !hasLoggedPromptEvent.current) {
      hasLoggedPromptEvent.current = true;
      logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_EMAIL_PROMPTED, {
        platform: Platform.isMobile ? "native" : "web",
      });
    }
  }, [authState]);

  // When Universal Links don't fire (e.g. Gmail/Outlook embedded webviews),
  // build a custom-scheme URL so the user can tap "Open in App" manually.
  const appSchemeUrl = useMemo(() => {
    if (!Platform.isMobileBrowser || !payload) {
      return undefined;
    }
    const appParams = new URLSearchParams({ payload });
    const requestedUrl = searchParams.get("requestedUrl");
    if (requestedUrl) {
      appParams.set("requestedUrl", requestedUrl);
    }
    return `${APP_CUSTOM_SCHEME}://${EMAIL_LOGIN_LINK_PATH}?${appParams.toString()}`;
  }, [payload, searchParams]);

  // The "Continue to Clipboard" button on the confirm screen reads the cache
  // each click rather than capturing it at mount, so a TTL expiry between
  // mount and click correctly falls through to the prompt.
  const handleConfirmContinue = useCallback(() => {
    const cachedAuthContext = getMagicLinkAuthContext();
    if (!cachedAuthContext) {
      setAuthState("email-input");
      return;
    }
    void handleContinue(cachedAuthContext, "cache");
  }, [handleContinue]);

  const handleSwitchUser = useCallback(() => {
    const cachedAuthContext = getMagicLinkAuthContext();
    if (!cachedAuthContext) {
      setAuthState("email-input");
      return;
    }
    void handleSignOutAndContinue(cachedAuthContext);
  }, [handleSignOutAndContinue]);

  if (Platform.isMobile) {
    if (authState === "email-input") {
      return <EmailInputView emailInputError={emailInputError} onSubmitEmail={handleSubmitEmail} />;
    }

    return (
      <MobileAuthLoading
        error={authState === "error" ? (error ?? "Sign in failed. Please try again.") : undefined}
        onRetry={() => {
          history.replace("/login");
        }}
      />
    );
  }

  return (
    <AuthRedirectView
      authState={authState}
      error={error}
      existingEmail={existingEmail}
      magicLinkEmail={getMagicLinkEmail()}
      appSchemeUrl={appSchemeUrl}
      emailInputError={emailInputError}
      onContinue={handleConfirmContinue}
      onSignOutAndContinue={handleSwitchUser}
      onSubmitEmail={handleSubmitEmail}
      onGoToDashboard={handleGoToDashboard}
    />
  );
}
